import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge } from '@/design-system/badge';
import { Button } from '@/design-system/button';
import { Card } from '@/design-system/card';
import { ChartCard } from '@/design-system/chart-card';
import { CompareBarChart, ComboChart } from '@/design-system/charts';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/design-system/select';
import { TableData, TableDataColumn } from '@/design-system/table-data';
import { Tabs, TabsList, TabsTrigger } from '@/design-system/tabs';
import { toast } from '@/design-system/toaster';
import {
  dashboardAPI,
  type DashboardWindow,
  type HubDetailResponse,
  type HubOverviewResponse,
  type StageTimelineResponse,
  type ZoneScanTimeRow,
} from '@/services/api/dashboard';
import { useUrlParam } from '@/hooks/urlState';

import CoverageCard from './components/CoverageCard';
import {
  fmtDuration,
  fmtInt,
  fmtSecPerItem,
  shortDate,
  VOLUME_STRESS_LABELS,
  VOLUME_STRESS_STATUS,
} from './components/format';
import OverviewTable from './components/OverviewTable';
import StageWaterfall from './components/StageWaterfall';
import VelocityTrendCard from './components/VelocityTrendCard';

const WINDOWS: { key: DashboardWindow; label: string }[] = [
  { key: '1d', label: '1d' },
  { key: '7d', label: '7d' },
  { key: '30d', label: '30d' },
  { key: '60d', label: '60d' },
];

const WINDOW_KEYS = WINDOWS.map((w) => w.key) as readonly DashboardWindow[];

const TREND_ICON: Record<'up' | 'down' | 'flat', string> = {
  up: 'ri-arrow-right-up-line',
  down: 'ri-arrow-right-down-line',
  flat: 'ri-subtract-line',
};

const DashboardPage: React.FC = () => {
  // Time window + hub selection live in the URL (shareable, back-button aware).
  // No pagination here. Window switches use push history.
  const [window, setWindow] = useUrlParam<DashboardWindow>('window', '7d', {
    allowed: WINDOW_KEYS,
    history: 'push',
  });
  const [hubParam, setHubParam] = useUrlParam<string>('hub', '');
  const hubId = hubParam === '' ? null : Number(hubParam);
  const setHubId = useCallback(
    (id: number | null) => setHubParam(id == null ? '' : String(id)),
    [setHubParam]
  );

  const [overview, setOverview] = useState<HubOverviewResponse | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(true);

  const [detail, setDetail] = useState<HubDetailResponse | null>(null);
  const [timeline, setTimeline] = useState<StageTimelineResponse | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const fetchOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      setOverview(await dashboardAPI.hubs({ window }));
    } catch (err) {
      setOverview(null);
      toast.error('Could not load the dashboard overview', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setOverviewLoading(false);
    }
  }, [window]);

  const fetchDetail = useCallback(async () => {
    if (hubId == null) return;
    setDetailLoading(true);
    try {
      const [d, t] = await Promise.all([
        dashboardAPI.hubDetail(hubId, { window }),
        dashboardAPI.stageTimeline(hubId, { window }),
      ]);
      setDetail(d);
      setTimeline(t);
    } catch (err) {
      setDetail(null);
      setTimeline(null);
      toast.error('Could not load the hub detail', {
        description: err instanceof Error ? err.message : 'Unknown error',
      });
    } finally {
      setDetailLoading(false);
    }
  }, [hubId, window]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const freshness = hubId != null ? detail?.freshness : overview?.freshness;
  const selectedRow = useMemo(
    () => overview?.rows.find((r) => r.hubId === hubId) ?? null,
    [overview, hubId]
  );

  const refresh = () => {
    fetchOverview();
    fetchDetail();
  };

  const zoneColumns: TableDataColumn<ZoneScanTimeRow>[] = [
    {
      key: 'zoneName',
      title: 'Zone',
      render: (_v, z) => (
        <span className="text-[13px] tracking-tight text-text-strong-950">
          {z.unassigned ? 'Unassigned' : z.zoneName}
        </span>
      ),
    },
    {
      key: 'itemCount',
      title: 'Items',
      render: (_v, z) => (
        <span className="font-mono text-[12px] tabular-nums text-text-sub-600">
          {fmtInt(z.itemCount)}
        </span>
      ),
    },
    {
      key: 'medianSec',
      title: 'Scan time (min / med / max)',
      render: (_v, z) => (
        <span className="font-mono text-[12px] tabular-nums text-text-sub-600">
          {fmtDuration(z.minSec)} / {fmtDuration(z.medianSec)} /{' '}
          {fmtDuration(z.maxSec)}
        </span>
      ),
    },
    {
      key: 'maSecPerItem',
      title: 'Velocity (MA)',
      render: (_v, z) => (
        <span className="inline-flex items-center gap-1 font-mono text-[12px] tabular-nums text-text-strong-950">
          {fmtSecPerItem(z.maSecPerItem)}
          <i
            className={`${TREND_ICON[z.trendDirection]} text-[12px] text-text-soft-400`}
          />
        </span>
      ),
    },
  ];

  return (
    <div className="space-y-5 p-6">
      <header className="flex flex-col gap-3 border-b border-stroke-soft-200 pb-5 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <p className="label-eyebrow mb-2">
            <span className="text-accent font-bold mr-3">01</span>
            Hub ops · Performance
          </p>
          <h1 className="text-[28px] font-extrabold tracking-tight text-text-strong-950 leading-none">
            Docking dashboard
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-text-sub-600 leading-relaxed">
            How fast packages move through each hub stage — inbound scan,
            dispatch, rider handover — with volume context. Daily snapshots,
            WIB dates.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Tabs
            value={window}
            onValueChange={(v) => setWindow(v as DashboardWindow)}
          >
            <TabsList variant="line" className="w-auto border-0">
              {WINDOWS.map((w) => (
                <TabsTrigger key={w.key} value={w.key}>
                  {w.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
          <Select
            value={hubId == null ? 'all' : String(hubId)}
            onValueChange={(v) => setHubId(v === 'all' ? null : Number(v))}
          >
            <SelectTrigger
              size="md"
              className="w-auto min-w-[180px]"
              aria-label="Select hub"
            >
              <SelectValue placeholder="All hubs" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All hubs</SelectItem>
              {(overview?.rows ?? []).map((r) => (
                <SelectItem key={r.hubId} value={String(r.hubId)}>
                  {r.hubName}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button tone="neutral" style="stroke" onClick={refresh}>
            <i className="ri-refresh-line mr-1.5 text-[14px]" />
            Refresh
          </Button>
        </div>
      </header>

      {freshness?.stale && (
        <div className="flex items-center gap-2 rounded-sm border border-warning-base/30 bg-warning-lighter px-4 py-2.5 text-[13px] text-text-strong-950">
          <i className="ri-time-line text-warning-base" />
          Data through {freshness.dataAsOf ?? '—'} — the latest daily snapshot
          hasn&apos;t landed yet.
        </div>
      )}

      {hubId == null ? (
        <Card padding="none">
          <div className="flex items-center justify-between border-b border-stroke-soft-200 px-4 py-3">
            <div>
              <h2 className="text-base font-bold tracking-tight text-text-strong-950">
                All hubs
              </h2>
              <p className="mt-0.5 text-xs text-text-sub-600">
                Sorted comparisons lead with velocity (sec/item) — volume is
                context, never the comparison number.
              </p>
            </div>
            {freshness && (
              <span className="font-mono text-[11px] tabular-nums text-text-soft-400">
                {freshness.windowStart} → {freshness.windowEnd} WIB
              </span>
            )}
          </div>
          <OverviewTable
            rows={overview?.rows ?? []}
            loading={overviewLoading}
            onSelectHub={setHubId}
          />
        </Card>
      ) : (
        <div className="space-y-5">
          <div className="flex flex-wrap items-center gap-3">
            <Button
              tone="neutral"
              style="stroke"
              size="xs"
              onClick={() => setHubId(null)}
            >
              <i className="ri-arrow-left-line mr-1 text-[13px]" />
              All hubs
            </Button>
            <h2 className="text-base font-bold tracking-tight text-text-strong-950">
              {detail?.hubName ?? selectedRow?.hubName ?? `Hub ${hubId}`}
            </h2>
            {detail?.volumeStress.band && (
              <Badge
                status={VOLUME_STRESS_STATUS[detail.volumeStress.band]}
                type="dot"
              >
                Volume {VOLUME_STRESS_LABELS[detail.volumeStress.band]}
                {detail.volumeStress.ratio != null &&
                  ` · ${Math.round(detail.volumeStress.ratio * 100)}% of p90`}
              </Badge>
            )}
            {detail?.coverage.lowCoverage && (
              <Badge status="warning" type="dot">
                Low coverage
              </Badge>
            )}
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <VelocityTrendCard
              eyebrow="Stage 1 · velocity"
              title="Inbound scan"
              subtitle={
                detail
                  ? `${fmtInt(detail.throughput.totals.scanned)} pkgs scanned`
                  : undefined
              }
              status={detail?.inbound.status ?? 'INSUFFICIENT_SAMPLES'}
              currentMa={detail?.inbound.currentMaSecPerItem ?? null}
              trend={
                detail?.inbound.trend ?? {
                  direction: 'flat',
                  percentChange: null,
                  priorMa: null,
                }
              }
              days={detail?.inbound.days ?? []}
              loading={detailLoading}
            />
            <VelocityTrendCard
              eyebrow="Stage 2 · velocity"
              title="Dispatch"
              subtitle="Committed − max(preview created, last inbound scan)"
              status={detail?.dispatch.status ?? 'INSUFFICIENT_SAMPLES'}
              currentMa={detail?.dispatch.currentMaSecPerItem ?? null}
              trend={
                detail?.dispatch.trend ?? {
                  direction: 'flat',
                  percentChange: null,
                  priorMa: null,
                }
              }
              days={detail?.dispatch.days ?? []}
              loading={detailLoading}
            />
            <VelocityTrendCard
              eyebrow="Stage 3 · velocity"
              title="Rider scan"
              subtitle={
                detail
                  ? `Scan gap/item: min ${fmtDuration(detail.riderScan.distribution.minSec)} · med ${fmtDuration(detail.riderScan.distribution.medianSec)} · max ${fmtDuration(detail.riderScan.distribution.maxSec)}`
                  : undefined
              }
              status={detail?.riderScan.status ?? 'INSUFFICIENT_SAMPLES'}
              currentMa={detail?.riderScan.currentMaSecPerItem ?? null}
              trend={
                detail?.riderScan.trend ?? {
                  direction: 'flat',
                  percentChange: null,
                  priorMa: null,
                }
              }
              days={detail?.riderScan.days ?? []}
              loading={detailLoading}
              footer={
                detail && detail.riderScan.arrivalLag.sampleSize > 0 ? (
                  <div className="mt-3 flex items-center justify-between border-t border-gray-100 pt-2 text-[11px] font-mono tabular text-gray-500">
                    <span>
                      Rider arrival lag · med{' '}
                      {fmtDuration(detail.riderScan.arrivalLag.medianSec)} ·
                      max {fmtDuration(detail.riderScan.arrivalLag.maxSec)}
                    </span>
                    <span
                      className={
                        detail.riderScan.arrivalLag.lateBatchCount > 0
                          ? 'font-semibold text-amber-600'
                          : ''
                      }
                    >
                      {detail.riderScan.arrivalLag.lateBatchCount}/
                      {detail.riderScan.arrivalLag.sampleSize} late (&gt;
                      {Math.round(
                        detail.riderScan.arrivalLag.lateThresholdSec / 60
                      )}
                      m)
                    </span>
                  </div>
                ) : undefined
              }
            />
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <StageWaterfall
              timeline={timeline}
              loading={detailLoading}
            />
            <CoverageCard
              coverage={detail?.coverage ?? null}
              loading={detailLoading}
            />
            <ChartCard
              eyebrow="Batch planner"
              title="Planner stress"
              subtitle="Unbatchable + deferred items per day"
              value={
                detail?.plannerStress.currentMaTotal != null
                  ? detail.plannerStress.currentMaTotal.toFixed(1)
                  : '—'
              }
              state={
                detailLoading
                  ? 'loading'
                  : detail && detail.plannerStress.days.length > 0
                    ? 'ready'
                    : 'empty'
              }
            >
              <ComboChart
                data={(detail?.plannerStress.days ?? []).map((d) => ({
                  date: shortDate(d.date),
                  total: d.total,
                  ma: d.maTotal,
                }))}
                xKey="date"
                bars={[{ key: 'total', label: 'items' }]}
                lines={[{ key: 'ma', label: '7-day MA' }]}
                height={180}
              />
            </ChartCard>
          </div>

          <div className="grid gap-4 lg:grid-cols-2">
            <ChartCard
              eyebrow="Volume"
              title="Throughput"
              subtitle={
                detail
                  ? `${fmtInt(detail.throughput.totals.scanned)} scanned · ${fmtInt(detail.throughput.totals.dispatched)} dispatched · ${fmtInt(detail.throughput.totals.handedOver)} handed over`
                  : undefined
              }
              state={
                detailLoading
                  ? 'loading'
                  : detail && detail.throughput.buckets.length > 0
                    ? 'ready'
                    : 'empty'
              }
            >
              <CompareBarChart
                data={(detail?.throughput.buckets ?? []).map((b) => ({
                  bucket: shortDate(b.bucketStart.slice(0, 10)),
                  scanned: b.scanned,
                  dispatched: b.dispatched,
                  handedOver: b.handedOver,
                }))}
                xKey="bucket"
                series={[
                  { key: 'scanned', label: 'Scanned' },
                  { key: 'dispatched', label: 'Dispatched' },
                  { key: 'handedOver', label: 'Handed over' },
                ]}
                height={220}
              />
            </ChartCard>

            <Card padding="none">
              <div className="border-b border-stroke-soft-200 px-4 py-3">
                <p className="label-eyebrow mb-1 text-[10px]">Zones</p>
                <h3 className="text-sm font-semibold tracking-tight text-text-strong-950">
                  Scan time by zone
                </h3>
              </div>
              <TableData<ZoneScanTimeRow>
                columns={zoneColumns}
                data={[...(detail?.zoneBreakdown.zoneScanTime ?? [])].sort(
                  (a, b) => b.itemCount - a.itemCount
                )}
                loading={detailLoading}
                emptyState="No zone activity in this window"
              />
            </Card>
          </div>

          {detail && detail.pendingMetrics.length > 0 && (
            <p className="text-[11px] text-text-soft-400">
              Coming soon:{' '}
              {detail.pendingMetrics.map((m) => m.label).join(' · ')} —{' '}
              {detail.pendingMetrics[0].reason}.
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
